// src/components/TicketScheduleSection.jsx
// Per-client ticket scheduler panel — sits inside ClientDetails.
// Shows existing schedules; lets admins create, edit, toggle, delete, and
// manually fire them.  Board → Type → Subtype → Item cascade via CW MCP.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, Button, IconButton, Chip, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, TextField, Switch,
  FormControlLabel, Divider, CircularProgress, Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as RunIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { ticketSchedulesApi, cwApi } from '../services/api';
import { toast } from 'react-toastify';

// ── Constants ─────────────────────────────────────────────────────────────────

const TICKET_TYPES = ['Vulnerability Scan', 'Penetration Test', 'AD Audit', 'NAR'];
const FREQUENCIES  = ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Annually'];
const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];
const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' },
  { value: 3, label: 'March' },   { value: 4, label: 'April' },
  { value: 5, label: 'May' },     { value: 6, label: 'June' },
  { value: 7, label: 'July' },    { value: 8, label: 'August' },
  { value: 9, label: 'September'},{ value: 10, label: 'October' },
  { value: 11, label: 'November'},{ value: 12, label: 'December' },
];

// Quarterly month sets keyed by starting month of first quarter
const QUARTERLY_SETS = {
  1: [1, 4, 7, 10],   // Jan / Apr / Jul / Oct
  2: [2, 5, 8, 11],   // Feb / May / Aug / Nov
  3: [3, 6, 9, 12],   // Mar / Jun / Sep / Dec
};
const QUARTERLY_START_OPTIONS = [
  { value: 1, label: 'January  (Jan / Apr / Jul / Oct)' },
  { value: 2, label: 'February (Feb / May / Aug / Nov)' },
  { value: 3, label: 'March    (Mar / Jun / Sep / Dec)' },
];
// Default — Jan / Apr / Jul / Oct
const QUARTERLY_MONTHS = QUARTERLY_SETS[1];

const KYBER_PURPLE = '#6B3FA0';
const KYBER_DARK   = '#4A2B70';

const EMPTY_FORM = {
  ticket_type: '',
  frequency: '',
  run_day: '',
  run_months: [],
  preferred_start_time: '',
  preferred_end_time: '',
  summary: '',
  initial_description: '',
  board_id: '', board_name: '',
  status_id: '', status_name: '',
  type_id: '', type_name: '',
  subtype_id: '', subtype_name: '',
  item_id: '', item_name: '',
  assigned_to_id: '', assigned_to_name: '',
  is_active: true,
};

// ── Frequency description helper ──────────────────────────────────────────────
function freqLabel(sched) {
  const { frequency, run_day, run_months } = sched;
  if (frequency === 'Daily') return 'Every day';
  if (frequency === 'Weekly') {
    const dow = DAYS_OF_WEEK.find(d => d.value === Number(run_day));
    return `Every ${dow ? dow.label : '—'}`;
  }
  if (frequency === 'Monthly') return `Monthly on day ${run_day || '—'}`;
  if (frequency === 'Quarterly') {
    const months = (run_months || QUARTERLY_MONTHS).map(m => {
      const mo = MONTHS.find(x => x.value === Number(m));
      return mo ? mo.label.slice(0, 3) : m;
    });
    return `Quarterly · ${months.join(', ')} on day ${run_day || '—'}`;
  }
  if (frequency === 'Annually') {
    const months = (run_months || []).map(m => {
      const mo = MONTHS.find(x => x.value === Number(m));
      return mo ? mo.label.slice(0, 3) : m;
    });
    return `Annually · ${months.join(', ')} on day ${run_day || '—'}`;
  }
  return frequency || '—';
}

// ── Main component ─────────────────────────────────────────────────────────────
const TicketScheduleSection = ({ clientId, clientName }) => {
  const [schedules, setSchedules]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [modalOpen, setModalOpen]     = useState(false);
  const [editId, setEditId]           = useState(null);
  const [form, setForm]               = useState(EMPTY_FORM);
  const [saving, setSaving]           = useState(false);
  const [deleting, setDeleting]       = useState(null);
  const [running, setRunning]         = useState(null);

  // CW cascade state
  // boardOpts holds the full options object returned by /board-options:
  //   { statuses, types, subtypes, items, itemsBySubtype }
  // Everything after board-select is derived client-side from this cache.
  // Tracks which board ID we last intentionally requested options for.
  // Any getBoardOptions response whose boardId doesn't match is stale and ignored.
  const activeBoardId = useRef(null);

  const [boards, setBoards]           = useState([]);
  const [boardOpts, setBoardOpts]     = useState(null);  // cached options for selected board
  const [statuses, setStatuses]       = useState([]);
  const [types, setTypes]             = useState([]);
  const [subtypes, setSubtypes]       = useState([]);    // filtered by selected type
  const [items, setItems]             = useState([]);    // filtered by selected subtype
  const [members, setMembers]         = useState([]);
  const [cwLoading, setCwLoading]     = useState(false);
  const [cwError, setCwError]         = useState(null);

  // ── Data loading ─────────────────────────────────────────────────────────────
  const loadSchedules = useCallback(async () => {
    try {
      setLoading(true);
      const r = await ticketSchedulesApi.list(clientId);
      setSchedules(r.data.schedules || []);
    } catch {
      toast.error('Failed to load ticket schedules');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { loadSchedules(); }, [loadSchedules]);

  // Load boards once when modal opens; members load separately (non-blocking)
  const loadBoards = async () => {
    if (boards.length) return;
    try {
      setCwLoading(true);
      setCwError(null);
      const bRes = await cwApi.getBoards();
      setBoards(bRes.data.boards || []);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Unknown error';
      setCwError(msg);
    } finally {
      setCwLoading(false);
    }
    // Members load in background — failure doesn't block board cascade
    cwApi.getMembers().then(r => setMembers(r.data.members || [])).catch(() => {});
  };

  // ── Cascade helpers (all client-side after board loads) ──────────────────────

  const _subtypesForType = (opts, typeId) => {
    if (!opts || !typeId) return [];
    const tid = Number(typeId);
    return (opts.subtypes || []).filter(s => {
      const ids = s.typeAssociationIds || s.type_association_ids || [];
      return ids.length > 0 && ids.includes(tid);
    });
  };

  const _itemsForSubtype = (opts, subtypeId) => {
    if (!opts || !subtypeId) return [];
    const key = String(subtypeId);
    const scoped = (opts.itemsBySubtype || {})[key];
    // Fall back to full board item list if associations didn't resolve this subtype
    return (scoped && scoped.length > 0) ? scoped : (opts.items || []);
  };

  // Cascade step 1: board selected → one API call fetches everything; type/subtype/item
  // dropdowns stay empty until the user makes the preceding selection.
  const onBoardChange = async (boardId, boardName) => {
    activeBoardId.current = boardId;   // mark this as the intended board
    setForm(f => ({
      ...f,
      board_id: boardId, board_name: boardName,
      status_id: '', status_name: '',
      type_id: '', type_name: '',
      subtype_id: '', subtype_name: '',
      item_id: '', item_name: '',
    }));
    setBoardOpts(null);
    setStatuses([]); setTypes([]); setSubtypes([]); setItems([]);
    if (!boardId) return;
    try {
      const r = await cwApi.getBoardOptions(boardId);
      // Ignore if the user changed boards again while this was in flight
      if (activeBoardId.current !== boardId) return;
      const opts = r.data;
      setBoardOpts(opts);
      setStatuses(opts.statuses || []);
      setTypes(opts.types    || []);
      // subtypes and items stay empty until type / subtype are picked
    } catch {
      toast.error('Failed to load board details');
    }
  };

  // Cascade step 2: type selected → filter subtypes from cached board options (no API call)
  const onTypeChange = (typeId, typeName) => {
    setForm(f => ({
      ...f,
      type_id: typeId, type_name: typeName,
      subtype_id: '', subtype_name: '',
      item_id: '', item_name: '',
    }));
    setSubtypes(_subtypesForType(boardOpts, typeId));
    setItems([]);
  };

  // Cascade step 3: subtype selected → filter items from cached board options (no API call)
  const onSubtypeChange = (subtypeId, subtypeName) => {
    setForm(f => ({
      ...f,
      subtype_id: subtypeId, subtype_name: subtypeName,
      item_id: '', item_name: '',
    }));
    setItems(_itemsForSubtype(boardOpts, subtypeId));
  };

  // ── Modal open/close ──────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditId(null);
    setForm({ ...EMPTY_FORM });
    setBoardOpts(null);
    setStatuses([]); setTypes([]); setSubtypes([]); setItems([]);
    setModalOpen(true);
    loadBoards();
  };

  const openEdit = (sched) => {
    setEditId(sched.id);
    setForm({
      ticket_type:         sched.ticket_type || '',
      frequency:           sched.frequency || '',
      run_day:             sched.run_day ?? '',
      run_months:          Array.isArray(sched.run_months) ? sched.run_months : [],
      summary:             sched.summary || '',
      initial_description: sched.initial_description || '',
      board_id:            sched.board_id || '',
      board_name:          sched.board_name || '',
      status_id:           sched.status_id || '',
      status_name:         sched.status_name || '',
      type_id:             sched.type_id || '',
      type_name:           sched.type_name || '',
      subtype_id:          sched.subtype_id || '',
      subtype_name:        sched.subtype_name || '',
      item_id:             sched.item_id || '',
      item_name:           sched.item_name || '',
      assigned_to_id:      sched.assigned_to_id || '',
      assigned_to_name:    sched.assigned_to_name || '',
      preferred_start_time: sched.preferred_start_time || '',
      preferred_end_time:   sched.preferred_end_time   || '',
      is_active:           Boolean(sched.is_active),
    });
    // Clear stale cascade state so the dropdown disabled checks start clean.
    // Board options are loaded independently below — not chained on loadBoards —
    // so there is no race between the boards list fetch and the options fetch.
    setBoardOpts(null);
    setStatuses([]); setTypes([]); setSubtypes([]); setItems([]);
    setCwError(null);
    setModalOpen(true);
    // Load boards list (in case this is the first modal open)
    loadBoards();
    // Load cascade options for the saved board immediately, in parallel with loadBoards
    if (sched.board_id) {
      const requestedBoardId = Number(sched.board_id);
      activeBoardId.current = requestedBoardId;
      cwApi.getBoardOptions(requestedBoardId)
        .then(r => {
          // Ignore if user changed boards while this was loading
          if (activeBoardId.current !== requestedBoardId) return;
          const opts = r.data;
          setBoardOpts(opts);
          setStatuses(opts.statuses || []);
          setTypes(opts.types    || []);
          if (sched.type_id)    setSubtypes(_subtypesForType(opts, Number(sched.type_id)));
          if (sched.subtype_id) setItems(_itemsForSubtype(opts, Number(sched.subtype_id)));
        })
        .catch(err => {
          const msg = err?.response?.data?.error || err?.response?.data?.cwError || err?.message || 'Unknown error';
          console.error('Board options restore failed:', msg);
          setCwError(`Could not load CW options for saved board: ${msg}`);
        });
    }
  };

  const closeModal = () => { setModalOpen(false); setSaving(false); };

  // ── Save ──────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.ticket_type || !form.frequency || !form.summary.trim()) {
      toast.error('Ticket type, frequency, and summary are required');
      return;
    }
    // Auto-fill quarterly months if not set
    const payload = {
      ...form,
      client_id: clientId,
      run_day: form.run_day !== '' ? Number(form.run_day) : null,
      run_months:
        form.frequency === 'Quarterly' && !form.run_months.length
          ? QUARTERLY_MONTHS
          : form.run_months,
    };
    setSaving(true);
    try {
      if (editId) {
        await ticketSchedulesApi.update(editId, payload);
        toast.success('Schedule updated');
      } else {
        await ticketSchedulesApi.create(payload);
        toast.success('Schedule created');
      }
      closeModal();
      loadSchedules();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this schedule? CW tickets already created will not be affected.')) return;
    setDeleting(id);
    try {
      await ticketSchedulesApi.delete(id);
      toast.success('Schedule deleted');
      loadSchedules();
    } catch {
      toast.error('Delete failed');
    } finally {
      setDeleting(null);
    }
  };

  // ── Toggle active ─────────────────────────────────────────────────────────────
  const handleToggle = async (id) => {
    try {
      await ticketSchedulesApi.toggle(id);
      loadSchedules();
    } catch {
      toast.error('Toggle failed');
    }
  };

  // ── Run now ───────────────────────────────────────────────────────────────────
  const handleRunNow = async (id, summary) => {
    if (!window.confirm(`Create a CW ticket now for "${summary}"?`)) return;
    setRunning(id);
    try {
      const r = await ticketSchedulesApi.runNow(id);
      toast.success(`CW ticket #${r.data.cw_ticket_id} created`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create ticket');
    } finally {
      setRunning(null);
    }
  };

  // ── Form field helper ─────────────────────────────────────────────────────────
  const F = (field, val) => setForm(f => ({ ...f, [field]: val }));

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ mt: 4 }}>
      {/* Section header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScheduleIcon sx={{ color: KYBER_PURPLE }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: KYBER_DARK }}>
            Ticket Schedules
          </Typography>
          <Chip label={schedules.length} size="small"
                sx={{ bgcolor: '#F3EEFA', color: KYBER_PURPLE, fontWeight: 700 }} />
        </Box>
        <Button variant="contained" size="small" startIcon={<AddIcon />}
                onClick={openCreate}
                sx={{ bgcolor: KYBER_PURPLE, '&:hover': { bgcolor: KYBER_DARK } }}>
          Add Schedule
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} sx={{ color: KYBER_PURPLE }} />
        </Box>
      ) : schedules.length === 0 ? (
        <Card variant="outlined" sx={{ borderColor: '#E5D5F5' }}>
          <CardContent sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <ScheduleIcon sx={{ fontSize: 40, color: '#bdc3c7', mb: 1 }} />
            <Typography>No ticket schedules yet.</Typography>
            <Typography variant="caption">
              Add a schedule to automatically create ConnectWise tickets on a recurring basis.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {schedules.map(sched => (
            <Card key={sched.id} variant="outlined"
                  sx={{ borderColor: sched.is_active ? '#C5A8E0' : '#ddd',
                        opacity: sched.is_active ? 1 : 0.6 }}>
              <CardContent sx={{ py: '12px !important' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  {/* Active toggle */}
                  <Tooltip title={sched.is_active ? 'Disable schedule' : 'Enable schedule'}>
                    <Switch size="small" checked={Boolean(sched.is_active)}
                            onChange={() => handleToggle(sched.id)}
                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: KYBER_PURPLE },
                                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: KYBER_PURPLE } }} />
                  </Tooltip>

                  {/* Ticket type chip */}
                  <Chip label={sched.ticket_type} size="small"
                        sx={{ bgcolor: '#F3EEFA', color: KYBER_DARK, fontWeight: 600, fontSize: '0.75rem' }} />

                  {/* Summary */}
                  <Typography sx={{ flex: 1, fontSize: '0.9rem', fontWeight: 600, color: '#2c3e50', minWidth: 200 }}>
                    {sched.summary}
                  </Typography>

                  {/* Frequency */}
                  <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    {freqLabel(sched)}
                  </Typography>

                  {/* Board → Item path */}
                  {sched.board_name && (
                    <Typography variant="caption" sx={{ color: KYBER_PURPLE, whiteSpace: 'nowrap' }}>
                      {[sched.board_name, sched.type_name, sched.subtype_name, sched.item_name]
                        .filter(Boolean).join(' › ')}
                    </Typography>
                  )}

                  {/* Actions */}
                  <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
                    <Tooltip title="Run now — create a CW ticket immediately">
                      <span>
                        <IconButton size="small" onClick={() => handleRunNow(sched.id, sched.summary)}
                                    disabled={running === sched.id}>
                          {running === sched.id
                            ? <CircularProgress size={16} />
                            : <RunIcon fontSize="small" sx={{ color: KYBER_PURPLE }} />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(sched)}>
                        <EditIcon fontSize="small" sx={{ color: '#7f8c8d' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <span>
                        <IconButton size="small" onClick={() => handleDelete(sched.id)}
                                    disabled={deleting === sched.id}>
                          {deleting === sched.id
                            ? <CircularProgress size={16} />
                            : <DeleteIcon fontSize="small" sx={{ color: '#e74c3c' }} />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* ── Add / Edit Modal ─────────────────────────────────────────────────── */}
      <Dialog open={modalOpen} onClose={closeModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: KYBER_DARK, color: 'white', fontWeight: 700 }}>
          {editId ? 'Edit Schedule' : 'Add Ticket Schedule'}
          <Typography variant="caption" sx={{ display: 'block', color: '#C5A8E0', mt: 0.5 }}>
            {clientName}
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 2, pb: 2 }}>
          {cwLoading && (
            <Alert severity="info" sx={{ mb: 2 }}>Loading ConnectWise boards…</Alert>
          )}

          {/* ── Section: Schedule Setup ─────────────────────────────────────── */}
          <Typography variant="overline" sx={{ color: KYBER_PURPLE, fontWeight: 700, letterSpacing: 1 }}>
            Schedule Setup
          </Typography>

          {/* Row: Ticket Type | Frequency */}
          <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Ticket Type *</InputLabel>
              <Select value={form.ticket_type} label="Ticket Type *"
                      onChange={e => F('ticket_type', e.target.value)}>
                {TICKET_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Frequency *</InputLabel>
              <Select value={form.frequency} label="Frequency *"
                      onChange={e => {
                        const freq = e.target.value;
                        setForm(f => ({
                          ...f,
                          frequency: freq,
                          run_day: '',
                          run_months: freq === 'Quarterly' ? QUARTERLY_MONTHS : [],
                        }));
                      }}>
                {FREQUENCIES.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          {/* Row: frequency-dependent day/month pickers */}
          {form.frequency === 'Weekly' && (
            <Box sx={{ mb: 2 }}>
              <FormControl sx={{ width: '50%', pr: 1 }} size="small">
                <InputLabel>Day of Week</InputLabel>
                <Select value={form.run_day} label="Day of Week"
                        onChange={e => F('run_day', e.target.value)}>
                  {DAYS_OF_WEEK.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
          )}
          {['Monthly', 'Quarterly', 'Annually'].includes(form.frequency) && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
              <TextField label="Day of Month" type="number" size="small"
                         sx={{ width: 160 }}
                         inputProps={{ min: 1, max: 28 }}
                         value={form.run_day}
                         onChange={e => F('run_day', e.target.value)}
                         helperText="1–28 recommended" />
              {form.frequency === 'Annually' && (
                <FormControl sx={{ flex: 1 }} size="small">
                  <InputLabel>Month</InputLabel>
                  <Select value={form.run_months[0] || ''} label="Month"
                          onChange={e => F('run_months', [e.target.value])}>
                    {MONTHS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                  </Select>
                </FormControl>
              )}
              {form.frequency === 'Quarterly' && (
                <FormControl sx={{ flex: 1 }} size="small">
                  <InputLabel>Starting Month</InputLabel>
                  <Select
                    value={
                      // Detect which set is active by the first month in run_months
                      QUARTERLY_START_OPTIONS.find(
                        o => JSON.stringify(QUARTERLY_SETS[o.value]) === JSON.stringify([...(form.run_months || [])].sort((a,b)=>a-b))
                      )?.value || 1
                    }
                    label="Starting Month"
                    onChange={e => F('run_months', QUARTERLY_SETS[e.target.value])}
                  >
                    {QUARTERLY_START_OPTIONS.map(o => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          )}

          {/* ── Row: Preferred Availability Window ─────────────────────────── */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="Preferred Start Time"
              size="small"
              type="time"
              sx={{ flex: 1 }}
              value={form.preferred_start_time}
              onChange={e => F('preferred_start_time', e.target.value)}
              helperText="Default: 8:30 AM"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Preferred End Time"
              size="small"
              type="time"
              sx={{ flex: 1 }}
              value={form.preferred_end_time}
              onChange={e => F('preferred_end_time', e.target.value)}
              helperText="Default: 5:30 PM"
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* ── Section: Ticket Content ─────────────────────────────────────── */}
          <Typography variant="overline" sx={{ color: KYBER_PURPLE, fontWeight: 700, letterSpacing: 1 }}>
            Ticket Content
          </Typography>

          <TextField label="Ticket Summary *" size="small" fullWidth sx={{ mt: 1, mb: 2 }}
                     value={form.summary}
                     onChange={e => F('summary', e.target.value)}
                     placeholder={`e.g. Quarterly Vulnerability Scan — ${clientName}`} />

          <TextField label="Initial Description (optional)" size="small" fullWidth multiline rows={3}
                     sx={{ mb: 2 }}
                     value={form.initial_description}
                     onChange={e => F('initial_description', e.target.value)} />

          <Divider sx={{ mb: 2 }} />

          {/* ── Section: ConnectWise Settings ──────────────────────────────── */}
          <Typography variant="overline" sx={{ color: KYBER_PURPLE, fontWeight: 700, letterSpacing: 1 }}>
            ConnectWise Ticket Settings
          </Typography>
          {cwError && (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
              ConnectWise error: {cwError}
            </Alert>
          )}
          {!cwError && boards.length === 0 && !cwLoading && (
            <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
              No boards returned from ConnectWise. The CW fields below are optional — you can save the schedule without them.
            </Alert>
          )}

          {/* Row: Board | Status */}
          <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 2 }}>
            <FormControl fullWidth size="small" disabled={cwLoading || boards.length === 0}>
              <InputLabel>Board</InputLabel>
              <Select value={form.board_id} label="Board"
                      onChange={e => {
                        const b = boards.find(x => x.id === e.target.value);
                        onBoardChange(e.target.value, b?.name || '');
                      }}>
                <MenuItem value=""><em>— None —</em></MenuItem>
                {boards.map(b => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" disabled={!form.board_id || !statuses.length}>
              <InputLabel>Initial Status</InputLabel>
              <Select value={form.status_id} label="Initial Status"
                      onChange={e => {
                        const s = statuses.find(x => x.id === e.target.value);
                        setForm(f => ({ ...f, status_id: e.target.value, status_name: s?.name || '' }));
                      }}>
                <MenuItem value=""><em>— None —</em></MenuItem>
                {statuses.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          {/* Row: Type | Subtype */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl fullWidth size="small" disabled={!form.board_id || !types.length}>
              <InputLabel>Type</InputLabel>
              <Select value={form.type_id} label="Type"
                      onChange={e => {
                        const t = types.find(x => x.id === e.target.value);
                        onTypeChange(e.target.value, t?.name || '');
                      }}>
                <MenuItem value=""><em>— None —</em></MenuItem>
                {types.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" disabled={!form.type_id || !subtypes.length}>
              <InputLabel>Subtype</InputLabel>
              <Select value={form.subtype_id} label="Subtype"
                      onChange={e => {
                        const s = subtypes.find(x => x.id === e.target.value);
                        onSubtypeChange(e.target.value, s?.name || '');
                      }}>
                <MenuItem value=""><em>— None —</em></MenuItem>
                {subtypes.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          {/* Row: Item | Assigned To */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl fullWidth size="small" disabled={!form.subtype_id || !items.length}>
              <InputLabel>Item</InputLabel>
              <Select value={form.item_id} label="Item"
                      onChange={e => {
                        const it = items.find(x => x.id === e.target.value);
                        setForm(f => ({ ...f, item_id: e.target.value, item_name: it?.name || '' }));
                      }}>
                <MenuItem value=""><em>— None —</em></MenuItem>
                {items.map(it => <MenuItem key={it.id} value={it.id}>{it.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" disabled={!members.length}>
              <InputLabel>Assigned To (optional)</InputLabel>
              <Select value={form.assigned_to_id} label="Assigned To (optional)"
                      onChange={e => {
                        const m = members.find(x => (x.id || x.identifier) === e.target.value);
                        setForm(f => ({
                          ...f,
                          assigned_to_id: e.target.value,
                          assigned_to_name: m?.identifier || m?.name || '',
                        }));
                      }}>
                <MenuItem value=""><em>— Unassigned —</em></MenuItem>
                {members.map(m => (
                  <MenuItem key={m.id || m.identifier} value={m.id || m.identifier}>
                    {m.firstName ? `${m.firstName} ${m.lastName}` : m.identifier || m.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <FormControlLabel
            control={
              <Switch checked={form.is_active}
                      onChange={e => F('is_active', e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: KYBER_PURPLE },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: KYBER_PURPLE } }} />
            }
            label="Schedule active"
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeModal} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
                  sx={{ bgcolor: KYBER_PURPLE, '&:hover': { bgcolor: KYBER_DARK }, minWidth: 120 }}>
            {saving ? <CircularProgress size={18} color="inherit" /> : (editId ? 'Save Changes' : 'Create Schedule')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TicketScheduleSection;
